import {CommonModule} from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  effect,
  input,
  model,
  OnInit,
  signal
} from '@angular/core';
import {
  Alert,
  AlertService,
  AlertType,
  Button,
  ButtonComponent,
  ButtonService,
  FileInput,
  FileOutput,
  InputWrapperComponent
} from '@innovactions/frontend-library';
import {CompanyCareerFunctionLabelPipe} from '../../../pipe/company-career-function-label-pipe';
import {FormArray, FormBuilder, UntypedFormGroup,} from '@angular/forms';

import {DocumentDto} from '../../../dto/document-dto';
import {Profile} from '../../../service/profile';
import {DocumentSchema} from '../../../schema/input/document-schema';
import {DocumentType} from '../../../enum/document-type';
import {DocumentLevel} from "../../../enum/document-level";

@Component({
  selector: 'app-profile-document-options',
  standalone: true,
  imports: [
    CommonModule,
    InputWrapperComponent,
    ButtonComponent
  ],
  providers: [
    CompanyCareerFunctionLabelPipe
  ],
  templateUrl: './profile-document-options.html',
  styleUrl: './profile-document-options.scss',
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class ProfileDocumentOptions implements OnInit {

  protected formGroup = signal<UntypedFormGroup | undefined>(undefined);
  userId = input<string | undefined>();
  protected fileData = signal<string>('');
  protected showDocumentModal = false;
  protected editingDocument = false;

  protected documentSchema = new DocumentSchema();
  protected submitButton = new Button(
    undefined,
    'document-button-id',
    'btn-primary hover-effect-elevated transition-expand w-full mt-4',
    undefined,
    undefined,
    'Document opslaan',
    false,
    true,
    true,
  );

  parentFormGroup = input<UntypedFormGroup>();
  documentType = input.required<DocumentType>();
  placeholder = input<string>('Waarde...');
  name = input<string>('Opties');
  readOnly = input<boolean>(false);
  selected = model<DocumentDto[]>([]);

  protected documents = signal<{ document: DocumentDto, fileUrl: any }[]>([]);

  //MODAL
  protected currentDocument = signal<DocumentDto | undefined>(undefined);

  protected fileUrl = signal<{ url: string, file: File } | null>(null);

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly buttonService: ButtonService,
    private readonly profileService: Profile,
    private readonly alertService: AlertService,
    private readonly cdr: ChangeDetectorRef,
  ) {
    this.formGroup.set(this.formBuilder.group({}));

    effect(() => {
      const selectedItems = this.selected();
      if (selectedItems && selectedItems.length > 0 && this.documents().length === 0) {
        this.documents.set([...selectedItems.map(d => ({document: d, fileUrl: null}))]);
        this.syncFormGroupToSelection();
      }
    });
  }

  ngOnInit() {
    if (this.selected() && this.selected().length > 0) {
      this.documents.set([...this.selected().map(d => ({document: d, fileUrl: null}))]);
    }
    this.syncFormGroupToSelection();
  }

  private syncFormGroupToSelection() {
    const pfg = this.parentFormGroup();

    if (!pfg) return;

    const formArray = pfg.get("documents") as FormArray;
    if (!formArray) return;

    for (let i = formArray.length - 1; i >= 0; i--) {
      const docInArray = formArray.at(i).get('document')?.value;
      if (docInArray?.type === this.documentType()) {
        formArray.removeAt(i);
      }
    }

    this.documents().forEach(doc => {
      formArray.push(this.formBuilder.group({
        document: [{ ...doc.document }],
        fileUrl: [doc.fileUrl],
      }));
    });

    this.selected.set(this.documents().map(d => d.document));
  }

  protected addDocument(document: DocumentDto) {
    if (this.readOnly()) return;
    const current = this.documents();
    this.documents.set([...current, {document: document, fileUrl: this.fileUrl()}]);
    this.syncFormGroupToSelection();
    this.parentFormGroup()?.markAsDirty();
  }

  protected removeDocument(index: number) {
    if (this.readOnly()) return;
    const current = [...this.documents()];
    current.splice(index, 1);
    this.documents.set(current);
    this.syncFormGroupToSelection();
    this.parentFormGroup()?.markAsDirty();
  }

  protected handleFilePush(file: FileOutput) {
    const docData = URL.createObjectURL(file.data);
    this.fileData.set(docData);
  }

  protected saveDocument() {
    if (this.formGroup()?.invalid) {
      this.alertService.createAlert(new Alert(AlertType.WARN, "", "Niet alle velden zijn juist ingevuld", false));
      this.buttonService.enableButton(this.submitButton);
      return;
    }

    const id = this.currentDocument() ? this.currentDocument()!.id : null;
    const name = this.formGroup()?.controls['name'].value ?? "Nieuw document";
    const level = this.formGroup()?.controls['level']?.value ?? DocumentLevel.NA;
    const achievementDate = this.formGroup()?.controls['achievementDate'].value?.split('T')[0] || null;
    const expirationDate = this.formGroup()?.controls['expirationDate'].value?.split('T')[0] || null;
    const validityDuration = this.formGroup()?.controls['validityDuration'].value ?? null;
    const fileOutput: FileOutput | undefined =
      this.formGroup()?.controls['documentFile'].value?.at(0);

    const editing = this.editingDocument;
    const newDoc = new DocumentDto(
      id,
      expirationDate,
      name,
      level,
      achievementDate,
      validityDuration,
      false,
      this.documentType()
    );

    let file: File | undefined = undefined;
    let fileId = -1;
    if (fileOutput) {
      file = fileOutput.data;
      fileId = fileOutput.id;
      if (file && fileId == -1) {
        this.profileService.createDocumentUrl(file.type, this.userId()!, newDoc).subscribe({
          next: (docResponse) => {
            this.fileUrl.set({
              url: docResponse.url,
              file: file!
            });

            if (editing && id) {
              this.spliceDocument(id);
            }
            this.addDocument(docResponse.document);
          },
          error: () => {
            this.alertService.createAlert(new Alert(AlertType.ERROR, "", "Upload-URL voor het document kon niet worden opgehaald", false));
            this.buttonService.enableButton(this.submitButton);
          }
        });
      } else {
        if (editing && id) {
          this.spliceDocument(id);
          this.addDocument(newDoc);
        }
      }
    }
    this.closeDocumentModal();
  }

  protected spliceDocument(id: number) {
    const current = [...this.documents()];
    const indexToReplace = current.findIndex(d => d.document.id === id);

    if (indexToReplace !== -1) {
      current.splice(indexToReplace, 1);
      this.documents.set(current);
    }
  }

  protected loadDocument(entry: { document: DocumentDto, fileUrl: any }) {
    const doc = entry.document;
    this.documentSchema.setValues(doc, []);
    this.currentDocument.set(doc);

    this.cdr.detectChanges();
    this.formGroup()?.patchValue({
      name: doc.name ?? '',
      level: doc.level ?? null,
      achievementDate: doc.achievementDate ?? '',
      validityDuration: String(doc.validityDuration ?? ''),
      expirationDate: doc.expirationDate ?? '',
    }, { emitEvent: false });

    if (entry.fileUrl && entry.fileUrl.file) {
      const file = entry.fileUrl.file;
      Promise.resolve().then(() => {
        this.formGroup()?.controls['documentFile']?.setValue([file], { emitEvent: false });
        const fileOutput = new FileOutput(1, file.name, file);
        (this.documentSchema.getInputs()[5] as FileInput).files = [fileOutput];
      });
      return;
    }

    if (doc.id) {
      this.profileService.retrieveDocumentUrl(this.userId()!, Number(doc.id)).subscribe({
        next: async (url) => {
          if (url) {
            const blob = await (await fetch(url)).blob();
            const extension = blob.type.split('/')[1] || 'pdf';
            const fileName = `${doc.name}-${doc.expirationDate}.${extension}`;
            const file = new File([blob], fileName, {type: blob.type});

            this.formGroup()?.controls['documentFile'].setValue([file], {
              emitEvent: false,
            });
            const fileOutput = new FileOutput(1, file.name, file);
            (this.documentSchema.getInputs()[5] as FileInput).files = [
              fileOutput,
            ];
          }
        },
        error: () => {
          this.alertService.createAlert(new Alert(AlertType.ERROR, "", "Document laden mislukt", false));
          this.buttonService.enableButton(this.submitButton);
        }
      });
    }
  }

  protected openDocumentModal(entry: { document: DocumentDto, fileUrl: any }) {
    if (!entry || !entry.document) {
      return;
    }
    this.loadDocument(entry);
    this.buttonService.enableButton(this.submitButton);
    this.showDocumentModal = true;
    this.editingDocument = true;
  }

  protected openCreateDocumentModal() {
    this.buttonService.enableButton(this.submitButton);
    this.currentDocument.set(undefined);
    this.fileUrl.set(null);
    this.showDocumentModal = true;
    this.editingDocument = false;
  }

  protected closeDocumentModal() {
    this.showDocumentModal = false;
    this.editingDocument = false;
    this.currentDocument.set(undefined);
    this.fileUrl.set(null);
    this.formGroup()?.reset({
      name: '',
      level: null,
      achievementDate: '',
      validityDuration: '',
      expirationDate: '',
      documentFile: []
    });
    this.documentSchema.setInputs();
  }

  downloadDocument(entry: { document: DocumentDto, fileUrl: any }) {
    const doc = entry.document

    if (entry.fileUrl && entry.fileUrl.file) {
      const file = entry.fileUrl.file;
      const downloadUrl = window.URL.createObjectURL(file);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = file.name;
      link.click();
      window.URL.revokeObjectURL(downloadUrl);
      return;
    }

    if (doc.id == null) {
      this.alertService.createAlert(new Alert(AlertType.WARN, "", "Dit document kan nog niet gedownload worden.", false));
      return;
    }
    this.profileService.retrieveDocumentUrl(this.userId()!, Number(doc.id)).subscribe({
      next: async (url) => {
        if (url) {
          const response = await fetch(url);
          const blob = await response.blob();
          const extension = blob.type.split('/')[1] || 'pdf';
          const fileName = `${doc.name}-${doc.expirationDate}.${extension}`;
          const downloadUrl = window.URL.createObjectURL(blob);
          const link = document.createElement('a');

          link.href = downloadUrl;
          link.download = fileName;
          link.click();
          window.URL.revokeObjectURL(downloadUrl);
        }
      },
      error: () => {
        this.alertService.createAlert(new Alert(AlertType.WARN, "", "Dit document kan niet gedownload worden.", false));
      }
    })
  }
}
